import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  createFileRoute,
  Link,
  Navigate,
  Outlet,
  useMatchRoute,
  useNavigate,
} from "@tanstack/react-router";
import { Bell, CheckCheck, LogOut, Settings, Shield, Trash2, User, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { CreateClubModal } from "../../components/CreateClubModal";
import { ChatPopupManager } from "../../components/chat/ChatPopupManager";
import { DevOverridesProvider, useDevOverrides } from "../../components/dev/DevToolsContext";
import { DevToolsPanel } from "../../components/dev/DevToolsPanel";
import { MobileNav } from "../../components/MobileNav";
import { MatchFoundOverlay } from "../../components/match/MatchFoundOverlay";
import { NavbarQueuePopover } from "../../components/NavbarQueuePopover";
import { PartyButton } from "../../components/party/PartyButton";
import { PartyInvitePopup } from "../../components/party/PartyInvitePopup";
import {
  AppShell,
  Button,
  MobileMenuButton,
  Navbar,
  NavbarSection,
  Sidebar,
  SidebarItem,
} from "../../components/ui";
import { useMatchFound } from "../../hooks/useMatchFound";
import { PartyContext, useParty } from "../../hooks/useParty";
import { usePlatformRole } from "../../hooks/usePlatformRole";
import { usePresence } from "../../hooks/usePresence";
import { useSocket } from "../../hooks/useSocket";
import { signOut, useSession, type AppSessionUser } from "../../lib/auth-client";
import { getSocket } from "../../lib/socket";
import { trpc } from "../../lib/trpc";

export const Route = createFileRoute("/app")({
  component: AppLayout,
});

function AppLayout() {
  const { data: session, isPending: isSessionLoading } = useSession();
  const user = session?.user as AppSessionUser | undefined;

  // Redirect to sign-in if not authenticated
  if (!isSessionLoading && !user) {
    return <Navigate to="/sign-in" />;
  }

  if (isSessionLoading) {
    return <AppBootstrapLoading />;
  }

  // Block unverified email users (social logins are auto-verified)
  if (!user?.emailVerified) {
    return <Navigate to="/verify-email" search={{ callbackURL: "/app", token: "" }} />;
  }

  return <AuthenticatedAppLayout />;
}

function AppBootstrapLoading() {
  return (
    <div className="min-h-screen bg-surface-950">
      <div className="border-b border-surface-800/80 bg-surface-900/70">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:px-6">
          <div className="h-7 w-28 animate-pulse rounded bg-surface-700/40" />
          <div className="h-9 w-24 animate-pulse rounded-lg bg-surface-700/40" />
        </div>
      </div>

      <div className="mx-auto flex max-w-7xl gap-6 px-4 py-6 md:px-6">
        <div className="hidden w-64 shrink-0 space-y-3 lg:block">
          {[1, 2, 3, 4].map((item) => (
            <div key={item} className="h-11 animate-pulse rounded-xl bg-surface-800/60" />
          ))}
        </div>

        <div className="min-w-0 flex-1 space-y-5">
          <div className="h-10 w-52 animate-pulse rounded-xl bg-surface-800/60" />
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {[1, 2, 3].map((item) => (
              <div key={item} className="h-40 animate-pulse rounded-2xl bg-surface-800/60" />
            ))}
          </div>
          <div className="h-64 animate-pulse rounded-2xl bg-surface-800/60" />
        </div>
      </div>
    </div>
  );
}

function AuthenticatedAppLayout() {
  // Track user presence (sets status to "online")
  usePresence();
  // Connect socket
  useSocket();

  const matchRoute = useMatchRoute();
  const { data: session } = useSession();
  const user = session?.user as AppSessionUser | undefined;
  const [createClubOpen, setCreateClubOpen] = useState(false);

  // Match found modal (global — works from any page)
  const matchFoundHook = useMatchFound();

  // Party state — single instance for the entire app tree
  const partyState = useParty();
  const {
    pendingInvite,
    setPendingInvite,
    dismissInvite,
    acceptInvite,
    isInParty,
    isAcceptingInvite,
  } = partyState;

  // Party member IDs for the match found overlay (identify party vs anonymous players)
  const partyPlayerIds = useMemo(() => {
    const members = partyState.party?.members;
    if (!members || members.length === 0) return undefined;
    return new Set(members.map((m: { userId: string }) => m.userId));
  }, [partyState.party?.members]);

  const isOnboarding = !!matchRoute({ to: "/app/onboarding", fuzzy: true });
  const isMatchPage = !!matchRoute({ to: "/app/match/$shortId", fuzzy: true });
  const isNodePreview = !!matchRoute({ to: "/app/dev/node-preview", fuzzy: true });

  // Redirect to onboarding if not completed
  if (user && !user.onboardingCompletedAt && !isOnboarding) {
    return <Navigate to="/app/onboarding/step-1" />;
  }

  // Redirect away from onboarding if already completed
  if (user?.onboardingCompletedAt && isOnboarding) {
    return <Navigate to="/app" />;
  }

  // If on onboarding route, render Outlet directly (no app shell)
  if (isOnboarding) {
    return <Outlet />;
  }

  // Full-screen immersive pages (no sidebar/navbar)
  const content =
    isMatchPage || isNodePreview ? (
      <>
        <Outlet />
        {import.meta.env.DEV && !isNodePreview && (
          <div className="fixed bottom-4 right-4 z-50">
            <DevToolsPanel dropUp />
          </div>
        )}
      </>
    ) : (
      <>
        <AppShell
          sidebar={<AppSidebar onCreateClub={() => setCreateClubOpen(true)} />}
          navbar={<AppNavbar onPartyInviteClick={setPendingInvite} />}
          mobileNav={<MobileNav onCreateClub={() => setCreateClubOpen(true)} />}
        >
          <Outlet />
        </AppShell>
        <CreateClubModal open={createClubOpen} onClose={() => setCreateClubOpen(false)} />
      </>
    );

  return (
    <PartyContext.Provider value={partyState}>
      <DevOverridesProvider>
        {content}
        <ChatPopupManager />
        <MatchFoundWithDevOverride
          matchFoundHook={matchFoundHook}
          currentUserId={user?.id}
          partyPlayerIds={partyPlayerIds}
        />
        <PartyInvitePopup
          invite={isInParty ? null : pendingInvite}
          isAccepting={isAcceptingInvite}
          onAccept={(partyId) => acceptInvite(partyId)}
          onDismiss={dismissInvite}
        />
      </DevOverridesProvider>
    </PartyContext.Provider>
  );
}

// ── Match Found Overlay with Dev Override ──────────────────────────────

function MatchFoundWithDevOverride({
  matchFoundHook,
  currentUserId,
  partyPlayerIds,
}: {
  matchFoundHook: ReturnType<typeof useMatchFound>;
  currentUserId?: string;
  partyPlayerIds?: Set<string>;
}) {
  const { overrides, setMatchFoundOverride } = useDevOverrides();
  const devPreset = overrides.matchFoundOverride;

  if (devPreset) {
    const devPartyIds = new Set(
      devPreset.players.filter((p) => p.isPartyMember).map((p) => p.userId),
    );
    const devAccepted = new Set(
      devPreset.players.filter((p) => p.acceptStatus === "accepted").map((p) => p.userId),
    );
    return (
      <MatchFoundOverlay
        open
        matchData={{ players: devPreset.players, status: "pending_accept" }}
        countdown={devPreset.countdown}
        acceptProgress={{ accepted: devAccepted, totalPlayers: devPreset.players.length }}
        cancelled={devPreset.cancelled}
        isAccepting={false}
        isDeclining={false}
        onAccept={() => setMatchFoundOverride(null)}
        onDecline={() => setMatchFoundOverride(null)}
        onDismiss={() => setMatchFoundOverride(null)}
        currentUserId="dev_1"
        partyPlayerIds={devPartyIds.size > 0 ? devPartyIds : undefined}
      />
    );
  }

  return (
    <MatchFoundOverlay
      open={!!matchFoundHook.matchFound}
      matchData={matchFoundHook.matchData ?? null}
      countdown={matchFoundHook.countdown}
      acceptProgress={matchFoundHook.acceptProgress}
      cancelled={matchFoundHook.cancelled}
      isAccepting={matchFoundHook.isAccepting}
      isDeclining={matchFoundHook.isDeclining}
      onAccept={matchFoundHook.acceptMatch}
      onDecline={matchFoundHook.declineMatch}
      currentUserId={currentUserId}
      partyPlayerIds={partyPlayerIds}
    />
  );
}

type SidebarClub = {
  id: string;
  name: string;
  slug: string;
  role: string;
  avatarUrl: string | null;
};

function AppSidebar({ onCreateClub }: { onCreateClub: () => void }) {
  const matchRoute = useMatchRoute();
  const utils = trpc.useUtils();
  const { data: clubs, isLoading } = trpc.club.getMyClubs.useQuery();
  const [activeClub, setActiveClub] = useState<SidebarClub | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const reorderMutation = trpc.club.reorderMyClubs.useMutation();

  const handleDragStart = (event: DragStartEvent) => {
    const club = clubs?.find((c) => c.id === event.active.id);
    if (club) setActiveClub(club);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveClub(null);
    const { active, over } = event;
    if (!over || active.id === over.id || !clubs) return;

    const oldIndex = clubs.findIndex((c) => c.id === active.id);
    const newIndex = clubs.findIndex((c) => c.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const previous = utils.club.getMyClubs.getData();
    const reordered = [...clubs];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);

    utils.club.getMyClubs.setData(undefined, reordered);
    reorderMutation.mutate(
      { clubIds: reordered.map((c) => c.id) },
      {
        onError: () => {
          if (previous) {
            utils.club.getMyClubs.setData(undefined, previous);
          } else {
            void utils.club.getMyClubs.invalidate();
          }
        },
        onSuccess: () => {
          void utils.club.getMyClubs.invalidate();
        },
      },
    );
  };

  const isDiscoverActive = !!matchRoute({ to: "/app/clubs" });

  return (
    <Sidebar
      logo={
        <Link to="/app">
          <img src="/logos/logo_icon.svg" alt="Rivaled" className="size-8" />
        </Link>
      }
      onAddClick={onCreateClub}
      discoverHref="/app/clubs"
      isDiscoverActive={isDiscoverActive}
    >
      {/* Clubs */}
      {isLoading
        ? [1, 2].map((i) => (
            <div key={i} className="w-full h-12 rounded-xl bg-surface-800 animate-pulse" />
          ))
        : clubs && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={clubs.map((c) => c.id)}
                strategy={verticalListSortingStrategy}
              >
                {clubs.map((club) => {
                  const isClubActive = !!matchRoute({
                    to: "/app/clubs/$slug",
                    params: { slug: club.slug },
                    fuzzy: true,
                  });
                  return <SortableSidebarItem key={club.id} club={club} isActive={isClubActive} />;
                })}
              </SortableContext>
              <DragOverlay>
                {activeClub && (
                  <div className="rounded-xl shadow-lg shadow-brand-500/30 ring-2 ring-brand-500">
                    <SidebarItem
                      label={activeClub.name}
                      subtitle={activeClub.role}
                      shortcut={activeClub.name.charAt(0).toUpperCase()}
                      isActive={false}
                      showTooltip={false}
                    >
                      {activeClub.avatarUrl && (
                        <img
                          src={activeClub.avatarUrl}
                          alt={activeClub.name}
                          className="w-10 h-10 rounded-lg object-cover"
                        />
                      )}
                    </SidebarItem>
                  </div>
                )}
              </DragOverlay>
            </DndContext>
          )}
    </Sidebar>
  );
}

function SortableSidebarItem({ club, isActive }: { club: SidebarClub; isActive: boolean }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: club.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <SidebarItem
        label={club.name}
        subtitle={club.role}
        shortcut={club.name.charAt(0).toUpperCase()}
        href={`/app/clubs/${club.slug}`}
        isActive={isActive}
      >
        {club.avatarUrl && (
          <img src={club.avatarUrl} alt={club.name} className="w-10 h-10 rounded-lg object-cover" />
        )}
      </SidebarItem>
    </div>
  );
}

type UserData = {
  id: string;
  name: string;
  slug?: string;
  image: string | null;
};

function UserMenu({ user }: { user?: UserData | null }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center justify-center size-8 rounded-full bg-surface-700 hover:bg-surface-600 transition-colors overflow-hidden ring-2 ring-transparent hover:ring-brand-500/30"
      >
        {user?.image ? (
          <img src={user.image} alt={user.name ?? ""} className="size-full object-cover" />
        ) : (
          <User className="size-4 text-surface-300" />
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-48 rounded-xl bg-surface-800 border border-surface-700/50 shadow-2xl z-50 overflow-hidden py-1">
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              void navigate({ to: "/app/profile" });
            }}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-surface-200 hover:bg-surface-700/50 transition-colors"
          >
            <User className="size-4" />
            Profile
          </button>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              void navigate({ to: "/app/settings", search: { tab: "general" } });
            }}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-surface-200 hover:bg-surface-700/50 transition-colors"
          >
            <Settings className="size-4" />
            Settings
          </button>
          <div className="border-t border-surface-700/50 my-1" />
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              void signOut({
                fetchOptions: { onSuccess: () => void navigate({ to: "/" }) },
              });
            }}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-error-400 hover:bg-surface-700/50 transition-colors"
          >
            <LogOut className="size-4" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

type PendingInvite = {
  partyId: string;
  inviterUsername: string;
  inviterAvatarUrl: string | null;
  clubName: string | null;
};

function AppNavbar({ onPartyInviteClick }: { onPartyInviteClick: (data: PendingInvite) => void }) {
  const { data: session } = useSession();
  const user = session?.user as AppSessionUser | undefined;
  const { isAdmin } = usePlatformRole();

  return (
    <Navbar>
      <NavbarSection>
        <div className="flex items-center gap-2 px-3">
          <img
            src="/logos/logo_icon.svg"
            alt="Rivaled"
            className="size-8 md:hidden"
            tabIndex={-1}
          />
          <MobileMenuButton />
        </div>
      </NavbarSection>

      <NavbarQueuePopover />

      <NavbarSection>
        <div className="flex items-center gap-3">
          {import.meta.env.DEV && <DevToolsPanel />}
          {isAdmin && (
            <Link
              to="/app/admin/games"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium text-brand-400 hover:text-brand-300 hover:bg-brand-500/10 transition-colors"
            >
              <Shield className="size-4" />
              <span className="hidden sm:inline">Admin</span>
            </Link>
          )}
          <PartyButton />
          <NotificationBell onPartyInviteClick={onPartyInviteClick} />

          {user?.slug && (
            <Link
              to="/app/users/$slug"
              params={{ slug: user.slug }}
              className="text-sm font-medium text-surface-200 hover:text-white transition-colors hidden sm:block"
            >
              {user.name}
            </Link>
          )}
          <UserMenu
            user={
              user
                ? { id: user.id, name: user.name, slug: user.slug, image: user.image ?? null }
                : undefined
            }
          />
        </div>
      </NavbarSection>
    </Navbar>
  );
}

function NotificationBell({
  onPartyInviteClick,
}: {
  onPartyInviteClick: (data: PendingInvite) => void;
}) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const { data: unreadData } = trpc.notification.getUnreadCount.useQuery(undefined, {
    refetchInterval: 30000,
  });

  const { data: notificationsData } = trpc.notification.list.useQuery(
    { limit: 5 },
    { enabled: open },
  );

  const markReadMutation = trpc.notification.markRead.useMutation({
    onSuccess: () => {
      void utils.notification.list.invalidate();
      void utils.notification.getUnreadCount.invalidate();
    },
  });

  const deleteMutation = trpc.notification.delete.useMutation({
    onSuccess: () => {
      void utils.notification.list.invalidate();
      void utils.notification.getUnreadCount.invalidate();
    },
  });

  const deleteAllMutation = trpc.notification.deleteAll.useMutation({
    onSuccess: () => {
      void utils.notification.list.invalidate();
      void utils.notification.getUnreadCount.invalidate();
    },
  });

  const count = unreadData?.count ?? 0;
  const notifications = notificationsData?.items ?? [];

  // Listen for real-time notifications via the shared socket singleton
  useEffect(() => {
    const s = getSocket();

    const handleNewNotification = () => {
      void utils.notification.getUnreadCount.invalidate();
      void utils.notification.list.invalidate();
    };

    s.on("notification:new", handleNewNotification);
    return () => {
      s.off("notification:new", handleNewNotification);
    };
  }, [utils]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="icon-md"
        className="relative text-surface-400 hover:text-white"
        aria-label="Notifications"
        onClick={() => setOpen(!open)}
      >
        <Bell className="size-5" />
        {count > 0 && (
          <span className="absolute top-1 right-1 flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-brand-500 text-xs font-bold text-surface-900">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 rounded-xl bg-surface-800 border border-surface-700/50 shadow-2xl z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-surface-700/50">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-white">Notifications</h3>
              <div className="flex items-center gap-2">
                {count > 0 && (
                  <button
                    type="button"
                    className="text-xs text-brand-400 hover:text-brand-300 font-medium flex items-center gap-1"
                    onClick={() => markReadMutation.mutate({})}
                  >
                    <CheckCheck className="size-3" />
                    Mark all read
                  </button>
                )}
                {notifications.length > 0 && (
                  <button
                    type="button"
                    className="text-xs text-surface-500 hover:text-error-400 font-medium flex items-center gap-1 transition-colors"
                    onClick={() => deleteAllMutation.mutate()}
                  >
                    <Trash2 className="size-3" />
                    Clear all
                  </button>
                )}
                <button
                  type="button"
                  className="text-xs text-surface-400 hover:text-surface-300 font-medium"
                  onClick={() => {
                    setOpen(false);
                    void navigate({ to: "/app/notifications" });
                  }}
                >
                  View all
                </button>
              </div>
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-8 text-center">
                <Bell className="size-6 text-surface-500 mx-auto mb-2" />
                <p className="text-sm text-surface-400">No notifications</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`group/notif relative flex items-start gap-3 px-4 py-3 border-b border-surface-700/30 last:border-b-0 hover:bg-surface-700/30 transition-colors ${
                    notification.isRead ? "opacity-60 hover:opacity-100" : ""
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => {
                      const raw = notification.data;
                      let d: Record<string, unknown> | null = null;
                      if (typeof raw === "string") {
                        try {
                          d = JSON.parse(raw);
                        } catch {
                          // malformed JSON — treat as no data
                        }
                      } else {
                        d = raw as Record<string, unknown> | null;
                      }

                      if (!notification.isRead) {
                        markReadMutation.mutate({
                          notificationId: notification.id,
                        });
                      }
                      setOpen(false);

                      // Party invite — show floating invite popup
                      if (notification.type === "party_invite") {
                        const partyId = typeof d?.partyId === "string" ? d.partyId : null;
                        if (partyId) {
                          const inviterUsername =
                            typeof d?.inviterUsername === "string"
                              ? d.inviterUsername
                              : notification.title.split(" invited ")[0] || "Someone";
                          onPartyInviteClick({
                            partyId,
                            inviterUsername,
                            inviterAvatarUrl:
                              typeof d?.inviterAvatarUrl === "string" ? d.inviterAvatarUrl : null,
                            clubName: typeof d?.clubName === "string" ? d.clubName : null,
                          });
                          return;
                        }
                      }

                      const clubSlug = typeof d?.clubSlug === "string" ? d.clubSlug : null;
                      const disputeId = typeof d?.disputeId === "string" ? d.disputeId : null;
                      if (clubSlug && disputeId) {
                        void navigate({
                          to: "/app/clubs/$slug/admin/disputes",
                          params: { slug: clubSlug },
                          search: {
                            status: "all_unresolved",
                            sort: "oldest",
                            claim: "all",
                            claimer: "all",
                            dispute: disputeId,
                          },
                        });
                        return;
                      }
                      if (clubSlug) {
                        void navigate({
                          to: "/app/clubs/$slug",
                          params: { slug: clubSlug },
                        });
                      }
                    }}
                    className="flex flex-1 items-start gap-3 text-left"
                  >
                    <div
                      className={`size-2 rounded-full mt-1.5 shrink-0 ${notification.isRead ? "bg-transparent" : "bg-brand-500"}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-surface-200 truncate">
                        {notification.title}
                      </p>
                      {notification.body && (
                        <p className="text-xs text-surface-500 truncate mt-0.5">
                          {notification.body}
                        </p>
                      )}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteMutation.mutate({ notificationId: notification.id });
                    }}
                    className="shrink-0 mt-0.5 rounded-md p-1 text-surface-600 opacity-0 transition-all hover:bg-surface-700/50 hover:text-surface-300 group-hover/notif:opacity-100"
                    aria-label="Dismiss notification"
                  >
                    <X className="size-3" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
